import React, {FC} from 'react';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {LensModel} from '../../../../models/api/lensModel';
import {NavigationLineChevron} from '../../../common/navigation/navigationLineChevron';
import {NavigationLineLabel} from '../../../common/navigation/navigationLineLabel';
import {TooltipOverflowCondition} from '../../../common/tooltips/__mocks__/tooltipOverflowCondition';
import {WorkspacesEditorRarelyViewedBadge} from '../editor/workspacesEditorRarelyViewedBadge';
import {WorkspaceLineDropTarget, WorkspaceLineDropTargetProps} from '../line/workspaceLineDropTarget';
import {WorkspaceLineMoreProps} from '../line/workspaceLineMore';
import {WorkspaceLineMoreContainer} from '../line/workspaceLineMoreContainer';
import {renderIconOrEmoji, StyledLabelDiv, StyledTextDiv} from '../workspaceCustomViewItem';

/*
 * Props.
 */

export interface LensesWorkspaceItemProps
  extends WorkspaceLineDropTargetProps,
    Omit<WorkspaceLineMoreProps, 'isGuidedDemoActive'> {
  /** The Lens. */
  lens: LensModel;
  /** Should the lens name be bold. */
  isUnread?: boolean;
  /** Whether the current item is currently expanded. */
  isExpanded?: boolean;
  /** Handler to collapse/expand this item. */
  onToggleExpandClick?: Handler;
  /** Should the checkbox be selected if we're in editing mode. */
  isChecked?: boolean;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  isRecentlyViewed?: boolean;
}

/*
 * Component.
 */

/** Display a lens as a P1 workspace line. */
export const LensesWorkspaceItem: FC<LensesWorkspaceItemProps> = (props) => {
  const {lens, isSelected} = props;

  return (
    <WorkspaceLineDropTarget
      {...props}
      shouldHidePreviewBadge={props.isEditingWorkspace}
      // TODO-LENS: implement detailed tooltip with lens ingredients etc.
      renderTooltip={() => lens.name}
      interactionId={`workspace-lens-${lens.id}`}
    >
      {maybeRenderChevron(props)}
      {renderIconOrEmoji(lens, isSelected)}
      <NavigationLineLabel isUnread={props.isUnread} isEditingWorkspace={props.isEditingWorkspace}>
        <StyledLabelDiv>
          <StyledTextDiv>
            <TooltipOverflowCondition>{lens.name}</TooltipOverflowCondition>
          </StyledTextDiv>
          {props.isRecentlyViewed === false && <WorkspacesEditorRarelyViewedBadge />}
        </StyledLabelDiv>
      </NavigationLineLabel>
      <WorkspaceLineMoreContainer
        renderMenu={props.renderMenu}
        badgeValue={props.badgeValue}
        isUnread={props.isUnread}
        loadingType={props.loadingType}
        shouldShowOfflineIcon={props.shouldShowOfflineIcon}
        isEditingWorkspace={props.isEditingWorkspace}
        isChecked={props.isChecked}
        shouldRemoveBadgeLeftSpacing={props.isRecentlyViewed === false}
      />
    </WorkspaceLineDropTarget>
  );
};

/*
 * Helpers.
 */

function maybeRenderChevron(props: LensesWorkspaceItemProps) {
  const {onToggleExpandClick} = props;
  if (!onToggleExpandClick) {
    return null;
  }

  return <NavigationLineChevron {...props} onToggleExpandClick={onToggleExpandClick} />;
}
